import React, { useContext, useEffect, useState } from "react";
import { EmbedType, PowerBIEmbed } from "powerbi-client-react";
import { ReportsContext } from "../stores";
import { models } from 'powerbi-client';
import { ReportNotFoundError } from "../errors";
import styles from "./ReportPage.module.scss";
import ReportNotFoundErrorContent from "../components/error/ReportNotFoundErrorContent";
import { useMsal } from "@azure/msal-react";
import Loading from "../components/loading/Loading";
import { useAppInsightsContext, useTrackEvent } from "@microsoft/applicationinsights-react-js";
import RefreshPermissionsDialog from "../components/refreshPermissionsDialog/RefreshPermissionsDialog";
import Error from "../components/error/Error";

export const ReportPage: React.FC = () => {

    const msal = useMsal();
    const appInsights = useAppInsightsContext();
    const reportsContext = useContext(ReportsContext);

    const getAccessTokenTrackData = () => {
        return {
            tokenValueLength: reportsContext.reportsAccessToken?.value?.length,
            tokenExpiration: reportsContext.reportsAccessToken?.expiration,
            tokenExpiresInSec: reportsContext.reportsAccessToken?.expiresInSec,
            tokenExpiredSecondAfter: reportsContext.reportsAccessToken?.expiredSecondAfter,
            tokenReceived: reportsContext.reportsAccessToken?.received
        }
    }

    const { tokenValueLength, tokenExpiration, tokenExpiresInSec, tokenExpiredSecondAfter, tokenReceived } = getAccessTokenTrackData();

    const trackReportViewedEvent = useTrackEvent(appInsights, "ReportViewed", reportsContext.report);
    const trackAccessTokenFetchedEvent = useTrackEvent(appInsights, "AccessTokenFetched", { tokenValueLength, tokenExpiration, tokenExpiresInSec, tokenExpiredSecondAfter, tokenReceived });

    const [modal, setModal] = useState<any>();

    const logout = async () => {
        await msal.instance.logoutRedirect({
            logoutHint: msal.accounts[0].idTokenClaims["login_hint"]
        });
    };

    const refreshPermissions = async () => {
        setModal(<RefreshPermissionsDialog refreshPermission={reportsContext.refreshPermissions} />);
    };


    useEffect(() => {
        const initialize = async () => {
            try {
                await Promise.all([
                    reportsContext.getReportsAccessToken(),
                    reportsContext.getReport(window.location.pathname)
                ]);
            } catch (error) {
                if (error instanceof ReportNotFoundError) {
                    setModal(<ReportNotFoundErrorContent
                        currentUserEmail={msal.accounts[0].username}
                        onLogoutButtonClick={logout}
                        onRefreshPermissionsClick={refreshPermissions}
                    />);
                } else {
                    setModal("Wystąpił problem podczas wczytywania raportu.");
                }
                document.title = "CUK Raporty | Błąd";
            }
        };

        document.title = "CUK Raporty | Wczytywanie...";
        initialize();
    }, []);

    useEffect(() => {
        if (reportsContext.report) {
            document.title = reportsContext.report.name;
            trackReportViewedEvent(reportsContext.report);
        }
    }, [reportsContext.report]);

    useEffect(() => {
        if (reportsContext.reportsAccessToken) {
            trackAccessTokenFetchedEvent(getAccessTokenTrackData());
        }
    }, [reportsContext.reportsAccessToken]);

    if (modal) {
        return <Error content={modal} />;
    }

    if (reportsContext.report === undefined || reportsContext.reportsAccessToken?.value === undefined || reportsContext.reportsAccessToken?.value === "") {
        return <Loading label="Wczytywanie raportu..." />;
    }

    return (
        <PowerBIEmbed
            embedConfig = {{
                type: EmbedType.Report,
                id: reportsContext.report.id,
                embedUrl: reportsContext.report.embedUrl,
                accessToken: reportsContext.reportsAccessToken.value,
                tokenType: models.TokenType.Aad,
                settings: {
                    panes: {
                        filters: {
                            expanded: false
                        }
                    }
                }
            }}
            cssClassName={styles.powerbiEmbed}
            eventHandlers = {
                new Map([
                    ['error', (event: any) => {
                        if (event?.detail?.message === "PowerBINotLicensedException" || event?.detail?.message === "ExplorationContainer_FailedToLoadModel_DefaultDetails") {
                            setModal("Brak licencji PowerBI PRO pozwalającej na wyświetlenie raportu poza godzinami 07:00 - 02:00. W przypadku dalszych problemów skontaktuj się z administratorem pod adresem raporty@cuk.pl.");
                        }
                    }]
                ])
            }
        />
    );
};
